import React from "react";
import { Menu } from "@headlessui/react";
import Skeleton from "react-loading-skeleton";
import { FileIcon } from "icons/document/FileIcon";
import { DownloadIcon } from "icons/payments/DownloadIcon";
import { EyeIcon } from "icons/payments/EyeIcon";
import { MoreIcon } from "icons/payments/MoreIcon";
import { formatPaymentListDate } from "utils/DateFormat";
import { DocumentAttachment } from "types/Documents";
import {
  TABLE_CONFIG,
  TABLE_COLUMN_NAMES,
  MENU_ITEMS,
  SPINNER_CLASS,
} from "constants/documents";

interface TableColumnsProps {
  isTableLoading: boolean;
  isDownloading: string | null;
  timezone?: string;
  onPreview: (data: DocumentAttachment) => void;
  onDownload: (path: string, fileName: string, id: string) => void;
  tid: (id: string) => string;
}

export const createTableColumns = ({
  isTableLoading,
  isDownloading,
  timezone = "UTC",
  onPreview,
  onDownload,
  tid,
}: TableColumnsProps) => [
  {
    id: "newTitle",
    name: TABLE_COLUMN_NAMES.NAME,
    maxwidth: TABLE_CONFIG.NAME_MAX_WIDTH,
    cell: (row: DocumentAttachment) => (
      <div>
        {isTableLoading ? (
          <Skeleton width={100} />
        ) : (
          <div
            className="flex items-center gap-2 cursor-pointer"
            role="button"
            tabIndex={0}
            data-testid={tid(`desktop-row-${row.id || row.title}-name`)}
            aria-label={`Preview ${row.newTitle || "document"}`}
            onClick={() => onPreview(row)}
          >
            <FileIcon aria-hidden="true" />
            {row.newTitle}
          </div>
        )}
      </div>
    ),
  },
  {
    id: "created_date",
    name: TABLE_COLUMN_NAMES.CREATED_DATE,
    maxwidth: TABLE_CONFIG.DATE_MAX_WIDTH,
    cell: (row: DocumentAttachment) => (
      <div>
        {isTableLoading ? (
          <Skeleton width={100} />
        ) : row.created_date ? (
          <span
            className="cursor-pointer"
            role="button"
            tabIndex={0}
            data-testid={tid(`desktop-row-${row.id || row.title}-date`)}
            aria-label={`Preview ${row.newTitle || "document"} by created date`}
            onClick={() => onPreview(row)}
          >
            {formatPaymentListDate(row.created_date, timezone, "short")}
          </span>
        ) : (
          "--"
        )}
      </div>
    ),
  },
  {
    id: "id",
    name: "",
    right: "true",
    cell: (row: DocumentAttachment) =>
      isTableLoading ? (
        <></>
      ) : (
        <div className="flex items-center gap-5 group">
          <div className="items-center gap-5 hidden icon-group">
            <div
              onClick={() => onPreview(row)}
              className="cursor-pointer"
              role="button"
              tabIndex={0}
              data-testid={tid(`desktop-row-${row.id || row.title}-preview`)}
              aria-label={`Preview ${row.newTitle || "document"}`}
            >
              <EyeIcon
                aria-hidden="true"
                className="group"
                _className="group-hover:!fill-iconObscure"
              />
            </div>
            <div
              onClick={(e) => {
                e.stopPropagation();
                onDownload(row.title, row.newTitle, row.id);
              }}
              className="cursor-pointer"
              role="button"
              tabIndex={0}
              data-testid={tid(`desktop-row-${row.id || row.title}-download`)}
              aria-label={`Download ${row.newTitle || "document"}`}
            >
              {isDownloading === row.id ? (
                <div className={SPINNER_CLASS} aria-hidden="true" />
              ) : (
                <DownloadIcon
                  aria-hidden="true"
                  className="group"
                  _className="group-hover:!stroke-iconObscure"
                />
              )}
            </div>
          </div>

          <Menu as="div" className="relative">
            <Menu.Button
              data-testid={tid(`desktop-row-${row.id || row.title}-actions`)}
              aria-label={`Open actions for ${row.newTitle || "document"}`}
            >
              <div className="hover:!bg-bgHover p-[9px] rounded-full cursor-pointer">
                <MoreIcon
                  aria-hidden="true"
                  className="group"
                  _className="group-hover:!fill-iconObscure"
                />
              </div>
            </Menu.Button>

            <Menu.Items className="absolute w-[153px] -left-[130px] top-[35px] !bg-bgPrimary hover:!bg-bgPrimary z-[1] rounded-[7px] drop-shadow-lg">
              <Menu.Item
                as="div"
                className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-t-[7px] cursor-pointer"
                data-testid={tid(
                  `desktop-row-${row.id || row.title}-menu-preview`
                )}
                aria-label={`Preview ${row.newTitle || "document"}`}
                onClick={() => onPreview(row)}
              >
                <EyeIcon width="16" height="16" aria-hidden="true" />{" "}
                {MENU_ITEMS.PREVIEW}
              </Menu.Item>
              <Menu.Item
                as="div"
                className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-b-[7px] cursor-pointer"
                data-testid={tid(
                  `desktop-row-${row.id || row.title}-menu-download`
                )}
                aria-label={`Download ${row.newTitle || "document"}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload(row.title, row.newTitle, row.id);
                }}
              >
                {isDownloading === row.id ? (
                  <div className={SPINNER_CLASS} aria-hidden="true" />
                ) : (
                  <DownloadIcon width="16" height="16" aria-hidden="true" />
                )}
                {isDownloading === row.id
                  ? MENU_ITEMS.DOWNLOADING
                  : MENU_ITEMS.DOWNLOAD}
              </Menu.Item>
            </Menu.Items>
          </Menu>
        </div>
      ),
  },
];
